/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState, useMemo } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { matchSorter } from 'match-sorter';

import { FlexCol, FlexRow, STATES, TERRITORIES, useBreakpoint } from 'sarsaparilla';
import FacilitiesList from './FacilitiesList';
import FacilitiesMap from './FacilitiesMap';

export interface Facility {
    name: string;
    id: string;
    label: string;
    value: string;
    state: string;
}

export interface GroupedFacilities {
    state: string;
    facilities: Facility[];
}

const getFacilities = async () => {
    const url = `${process.env.API}/parkpass/facilities`;
    const response = await axios.get(url);
    const data = response.data;
    return data;
};

export default function SitePassFacilities() {
    // Gets the facilities from the API and groups them by state
    const { data } = useQuery({
        queryKey: ['facilities'],
        queryFn: getFacilities,
        staleTime: Infinity,
    }); // Fetch facilities from the API

    const [filteredFacilities, setFilteredFacilities] = useState<GroupedFacilities[]>([]);

    const groupedFacilitiesByState = useMemo(() => {
        if (data) {
            const groupedFacilities = data.facilities.reduce(
                (acc: Record<string, Facility[]>, facilityData: Facility) => {
                    const stateName =
                        STATES.find((state) => state.value === facilityData.state)
                            ?.label ||
                        TERRITORIES.find(
                            (territory) => territory.value === facilityData.state
                        )?.label ||
                        'Unknown State';

                    if (!acc[stateName]) {
                        acc[stateName] = [];
                    }
                    acc[stateName].push({
                        ...facilityData,
                        state: stateName,
                        label: facilityData.name,
                        value: facilityData.name.replace(/ /g, '_').toLowerCase(),
                        id: facilityData.id,
                    });

                    return acc;
                },
                {}
            );
            // sort facilities within each state
            Object.keys(groupedFacilities).forEach((state) => {
                groupedFacilities[state].sort((a: { name: string }, b: { name: any }) =>
                    a.name.localeCompare(b.name)
                );
            });
            // convert to array of objects: [{ state: string, facilities: Facility[] }]
            const groupedFacilitiesArray = Object.keys(groupedFacilities)
                .sort()
                .map((state) => ({
                    state,
                    facilities: groupedFacilities[state],
                }));
            return groupedFacilitiesArray;
        }
        return [];
    }, [data]);

    const filterFacilities = (searchTerm: string) => {
        if (!searchTerm) {
            setFilteredFacilities([]);
            return;
        }

        // 1. Flatten all facilities with their state
        const allFacilities: Facility[] = groupedFacilitiesByState.flatMap((group) =>
            group.facilities.map((facility: Facility) => ({
                ...facility,
                state: group.state,
            }))
        );

        // 2. Filter using matchSorter on state and name
        const filteredFlat = matchSorter(allFacilities, searchTerm, {
            keys: ['state', 'name'],
            threshold: matchSorter.rankings.CONTAINS,
        });

        // 3. Regroup by state
        const regrouped: GroupedFacilities[] = [];
        filteredFlat.forEach((facility) => {
            let group = regrouped.find((g) => g.state === facility.state);
            if (!group) {
                group = { state: facility.state, facilities: [] };
                regrouped.push(group);
            }
            group.facilities.push(facility);
        });

        // Sort groups and facilities
        regrouped.sort((a, b) => a.state.localeCompare(b.state));
        regrouped.forEach((group) =>
            group.facilities.sort((a, b) => a.name.localeCompare(b.name))
        );

        setFilteredFacilities(regrouped);
    };

    // Choose which list to show
    const facilitiesToShow =
        filteredFacilities.length > 0 ? filteredFacilities : groupedFacilitiesByState;

    // For the map, we need the filtered facilities flattened out
    const facilitiesToShowForMap = useMemo(() => {
        if (filteredFacilities.length > 0) {
            return filteredFacilities.flatMap((group) => group.facilities);
        }
        return groupedFacilitiesByState.flatMap((group) => group.facilities);
    }, [filteredFacilities, groupedFacilitiesByState]);

    return (
        <FlexRow className="site-pass-facilities">
            <FlexCol xl={3}>
                <FacilitiesList
                    facilitiesList={facilitiesToShow}
                    onFilterFacilities={filterFacilities}
                />
            </FlexCol>
            {useBreakpoint().isXlAndUp && (
                <FlexCol>
                    <FacilitiesMap facilitiesList={facilitiesToShowForMap} />
                </FlexCol>
            )}
        </FlexRow>
    );
}
